import { useCallback, useEffect, useRef, useState } from "react";
import Hls from "hls.js";

const STORAGE_KEY = "ottkit:player";

interface StoredPrefs {
  volume?: number;
  muted?: boolean;
}

function readPrefs(): StoredPrefs {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(
      window.localStorage.getItem(STORAGE_KEY) || "{}"
    ) as StoredPrefs;
  } catch {
    return {};
  }
}

function writePrefs(prefs: StoredPrefs) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...readPrefs(), ...prefs })
    );
  } catch {
    // storage unavailable
  }
}

export interface VideoEngineLevel {
  index: number;
  height: number;
}

export interface VideoEngineState {
  currentTime: number;
  duration: number;
  paused: boolean;
  ended: boolean;
  isLoading: boolean;
  buffered: number;
  volume: number;
  muted: boolean;
  playbackRate: number;
  levels: VideoEngineLevel[];
  currentLevel: number;
  autoLevel: boolean;
  error: boolean;
  started: boolean;
  /** Живой HLS-плейлист (без EXT-X-ENDLIST): duration у video это лишь
   * скользящее окно сегментов, а не длительность эфира. */
  live: boolean;
}

export interface UseVideoEngineOptions {
  src?: string | null;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  startPosition?: number | null;
}

export function useVideoEngine(options: UseVideoEngineOptions) {
  const {
    src,
    autoPlay = false,
    muted = false,
    loop = false,
    startPosition,
  } = options;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [state, setState] = useState<VideoEngineState>({
    currentTime: 0,
    duration: 0,
    paused: true,
    ended: false,
    isLoading: false,
    buffered: 0,
    volume: 1,
    muted,
    playbackRate: 1,
    levels: [],
    currentLevel: -1,
    autoLevel: true,
    error: false,
    started: false,
    live: false,
  });

  const patch = useCallback((partial: Partial<VideoEngineState>) => {
    setState((prev) => ({ ...prev, ...partial }));
  }, []);

  // Attach source
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return undefined;
    patch({ error: false, ended: false });

    let hls: Hls | null = null;
    if (Hls.isSupported()) {
      const instance = new Hls({
        capLevelToPlayerSize: true,
        capLevelOnFPSDrop: true,
        maxBufferLength: 20,
        // Cloudflare Stream тарифицирует доставленные минуты, а hls.js по
        // умолчанию буферизует вперёд max(60 МБ / битрейт, 30 с): для 1080p
        // около двух минут. Как на витрине: потолок 40 с, сзади 30 с.
        maxMaxBufferLength: 40,
        backBufferLength: 30,
        startPosition: startPosition ?? -1,
      });
      instance.loadSource(src);
      instance.attachMedia(video);
      instance.on(Hls.Events.MANIFEST_PARSED, () => {
        patch({
          levels: instance.levels.map((level, index) => ({
            index,
            height: level.height,
          })),
        });
      });
      instance.on(Hls.Events.LEVEL_LOADED, (_event, data) => {
        patch({ live: Boolean(data.details.live) });
      });
      instance.on(Hls.Events.LEVEL_SWITCHED, (_event, data) => {
        patch({
          currentLevel: data.level,
          autoLevel: instance.autoLevelEnabled,
        });
      });
      instance.on(Hls.Events.ERROR, (_event, data) => {
        if (!data.fatal) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          instance.startLoad();
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          instance.recoverMediaError();
        } else {
          patch({ error: true, isLoading: false });
        }
      });
      hls = instance;
      hlsRef.current = instance;
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
      if (startPosition) video.currentTime = startPosition;
    } else {
      patch({ error: true });
    }

    return () => {
      hls?.destroy();
      hlsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  // Video element listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const prefs = readPrefs();
    video.volume = prefs.volume ?? 1;
    video.muted = muted || prefs.muted === true;
    video.loop = loop;
    patch({ volume: video.volume, muted: video.muted });

    const onTimeUpdate = () => patch({ currentTime: video.currentTime });
    const onDurationChange = () => patch({ duration: video.duration || 0 });
    const onProgress = () => {
      const { buffered } = video;
      patch({
        buffered: buffered.length ? buffered.end(buffered.length - 1) : 0,
      });
    };
    const onPlay = () => patch({ paused: false, ended: false, started: true });
    const onPause = () => patch({ paused: true });
    const onEnded = () => patch({ ended: true, paused: true });
    const onWaiting = () => patch({ isLoading: true });
    const onPlaying = () => patch({ isLoading: false });
    // Native <video> error (e.g. the Safari-native-HLS fallback path with no
    // hls.js involved at all, or any decode failure hls.js itself doesn't
    // intercept) — without this, playback silently stalls with no retry UI.
    const onError = () => patch({ error: true, isLoading: false });
    // Only patch state here — never persist. This fires for the mount-time
    // prefs restore and the autoplay-blocked mute fallback too, and those must
    // not be written back as if the user chose them. Persistence happens only
    // from the user-initiated actions below (setVolume / toggleMute).
    const onVolumeChange = () => {
      patch({ volume: video.volume, muted: video.muted });
    };
    const onRateChange = () => patch({ playbackRate: video.playbackRate });

    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("durationchange", onDurationChange);
    video.addEventListener("progress", onProgress);
    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("ended", onEnded);
    video.addEventListener("waiting", onWaiting);
    video.addEventListener("playing", onPlaying);
    video.addEventListener("volumechange", onVolumeChange);
    video.addEventListener("ratechange", onRateChange);
    video.addEventListener("error", onError);
    return () => {
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("durationchange", onDurationChange);
      video.removeEventListener("progress", onProgress);
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("ended", onEnded);
      video.removeEventListener("waiting", onWaiting);
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("volumechange", onVolumeChange);
      video.removeEventListener("ratechange", onRateChange);
      video.removeEventListener("error", onError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  const play = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    try {
      await video.play();
    } catch (error) {
      // Autoplay blocked: retry muted. This is a browser-imposed fallback, not a
      // user choice, so it must not be persisted as a preference (see
      // onVolumeChange above — it never persists).
      if ((error as DOMException)?.name === "NotAllowedError" && !video.muted) {
        video.muted = true;
        try {
          await video.play();
        } catch {
          // give up: user will press play
        }
      }
    }
  }, []);

  // Autoplay on mount / src change
  useEffect(() => {
    if (autoPlay && src) void play();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, autoPlay]);

  const actions = {
    play,
    pause: useCallback(() => videoRef.current?.pause(), []),
    togglePlay: useCallback(() => {
      const video = videoRef.current;
      if (!video) return;
      if (video.paused) void play();
      else video.pause();
    }, [play]),
    seek: useCallback((time: number) => {
      const video = videoRef.current;
      if (video)
        video.currentTime = Math.max(0, Math.min(time, video.duration || time));
    }, []),
    seekBy: useCallback((delta: number) => {
      const video = videoRef.current;
      if (video) video.currentTime = Math.max(0, video.currentTime + delta);
    }, []),
    setVolume: useCallback((volume: number) => {
      const video = videoRef.current;
      if (!video) return;
      video.volume = Math.max(0, Math.min(1, volume));
      if (video.volume > 0) video.muted = false;
      // User-initiated: persist the resulting volume/mute state.
      writePrefs({ volume: video.volume, muted: video.muted });
    }, []),
    toggleMute: useCallback(() => {
      const video = videoRef.current;
      if (!video) return;
      video.muted = !video.muted;
      // User-initiated: persist the resulting mute state.
      writePrefs({ volume: video.volume, muted: video.muted });
    }, []),
    setPlaybackRate: useCallback((rate: number) => {
      const video = videoRef.current;
      if (video) video.playbackRate = rate;
    }, []),
    setLevel: useCallback((index: number) => {
      const hls = hlsRef.current;
      if (hls) hls.currentLevel = index;
    }, []),
    retry: useCallback(() => {
      const video = videoRef.current;
      const hls = hlsRef.current;
      if (hls) {
        hls.startLoad();
      } else if (video) {
        video.load();
      }
    }, []),
  };

  return { videoRef, hlsRef, state, actions };
}
